import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useGrid } from "../../../context/GridContext";

const CHUNK_SIZE = 1000; // Adjust for performance
const loadImageAsBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export function useExcelExport() {
  const [isExporting, setIsExporting] = useState(false);
  const user = useSelector((state) => state.RS_Authentication.user);
  const { reportLenders } = useGrid();
  const isBroadviewAccount = user.details.email?.includes("broadview");

  const exportToExcel = useCallback(
    async ({ rows, headerGroups, selectedAccount, title }) => {
      let isInsuranceMonitiring = false;
      const headerArr = [];
      setIsExporting(true);
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Data");
        const imageBase64 = await loadImageAsBase64(
          "https://i.ibb.co/6B098Gh/loanlinq.png"
        );
        const imageId = workbook.addImage({
          base64: imageBase64,
          extension: "png",
        });

        worksheet.addImage(imageId, "A1:G5"); // Set the range where you want the image
        // Add title and account info
        worksheet.addRow([
          `${reportLenders ? reportLenders?.length > 2 ? "Multiple Accounts" : reportLenders.map((el) => el.label).join(", ") : selectedAccount?.label} | Superior Loan Servicing`,
        ]);
        worksheet.getCell("A7").font = { name: "Arial", size: 24 };
        worksheet.addRow([``]);
        worksheet.addRow([`${title}`]);
        worksheet.getCell("A9").font = { name: "Arial", size: 24 };
        worksheet.addRow([``]);

        // Add headers
        headerGroups?.forEach((group) => {
          const headerRow = group.headers.map((col) => {
            headerArr.push(col?.field);
            return typeof col?.Header === "string" ? col.Header : "";
          });
          if (headerRow === "Insurance Monitoring") {
            isInsuranceMonitiring = true;
          }
          worksheet.addRow(headerRow);
        });

        // Function to chunk and add rows
        const addRowsInChunks = async () => {
          for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
            const chunk = rows.slice(i, i + CHUNK_SIZE);
            for (const row of chunk) {
              if (row.subRows?.length) {
                row.subRows.forEach((sub) => {
                  worksheet.addRow(Object.values(sub.values));
                });
              } else {
                let newRowValues = { ...row.values };
                if (row.values?.insurance_info) {
                  const badges = {
                    Pending: {
                      count: 0,
                      bg: "yellow",
                      color: "black",
                      text: "Pending",
                    },
                    "Forced Placed": {
                      count: 0,
                      bg: "blue",
                      color: "white",
                      text: "Forced Placed",
                    },
                    Insured: {
                      count: 0,
                      bg: "green",
                      color: "white",
                      text: "Insured",
                    },
                    Expired: {
                      count: 0,
                      bg: "red",
                      color: "white",
                      text: "Expired",
                    },
                  };

                  const newBadges = {
                    "Expiring in 30 days": {
                      count: 0,
                      bg: "yellow",
                      color: "black",
                      text: "Expiring in 30 days",
                    },
                    "Forced Placed Expires in 30 days": {
                      count: 0,
                      bg: "red",
                      color: "white",
                      text: "Forced Placed Expires in 30 days",
                    },
                    Current: {
                      count: 0,
                      bg: "green",
                      color: "white",
                      text: "Current",
                    },
                    "Forced Placed Current": {
                      count: 0,
                      bg: "black",
                      color: "white",
                      text: "Forced Placed Current",
                    },
                    "No Proof / Lapse of Insurance": {
                      count: 0,
                      bg: "red",
                      color: "white",
                      text: "No Proof / Lapse of Insurance",
                    },
                    Pending: {
                      count: 0,
                      bg: "white",
                      color: "black",
                      text: "Pending",
                      border: "1px solid black",
                    },
                  };

                  // Update counts based on value array
                  row.values.insurance_info?.forEach((data) => {
                    if (data?.includes("yellow"))
                      newBadges["Expiring in 30 days"].count += 1;
                    else if (data?.includes("blue-green"))
                      newBadges["Forced Placed Current"].count += 1;
                    else if (data?.includes("green"))
                      newBadges.Current.count += 1;
                    else if (data?.includes("blue-yellow"))
                      newBadges["Forced Placed Expires in 30 days"].count += 1;
                    else if (data?.includes("red"))
                      newBadges["No Proof / Lapse of Insurance"].count += 1;
                    else if (data?.includes("Pending"))
                      newBadges.Pending.count += 1;
                  });
                  // if (isBroadviewAccount) {
                  //   row.values.insurance_info?.forEach((data) => {
                  //     if (data?.includes("yellow"))
                  //       newBadges["Expiring in 30 days"].count += 1;
                  //     else if (data?.includes("blue-green"))
                  //       newBadges["Forced Placed Current"].count += 1;
                  //     else if (data?.includes("green"))
                  //       newBadges.Current.count += 1;
                  //     else if (data?.includes("blue-yellow"))
                  //       newBadges[
                  //         "Forced Placed Expires in 30 days"
                  //       ].count += 1;
                  //     else if (data?.includes("red"))
                  //       newBadges["No Proof / Lapse of Insurance"].count += 1;
                  //     else if (data?.includes("Pending"))
                  //       newBadges.Pending.count += 1;
                  //   });
                  // } else {
                  //   row.values.insurance_info?.forEach((data) => {
                  //     if (data?.includes("Pending")) badges.Pending.count += 1;
                  //     else if (data?.includes("blue-green"))
                  //       badges["Forced Placed"].count += 1;
                  //     else if (data?.includes("green"))
                  //       badges.Insured.count += 1;
                  //     else badges.Expired.count += 1;
                  //   });
                  // }

                  // Function to generate badge components
                  const generateBadges = () =>
                    // Object.entries(isBroadviewAccount ? newBadges : badges)
                    Object.entries(newBadges)
                      .map(
                        ([, { count, text }]) =>
                          count
                            ? isBroadviewAccount
                              ? text
                              : `${count} ${text}`
                            : null
                        // count ? text : null
                      )
                      .filter(Boolean)
                      .join(", ");
                  newRowValues = {
                    ...row.values,
                    insurance_info: generateBadges(),
                  };
                }

                // const dataRow = Object.values(newRowValues).map((value) =>
                //   // value || value === "0" || value === 0 ? `${value}` : ""
                //   typeof value === "number" ? Number(value) : value
                // );

                const dataRow = [];
                Object.entries(newRowValues)?.forEach(([key, value]) => {
                  if (headerArr?.includes(key)) {
                    dataRow.push({
                      value,
                      type: typeof value === "number" ? "number" : "string",
                    });
                  }
                });

                worksheet.addRow(dataRow.map((data) => data.value));
              }
            }
            await new Promise((res) => setTimeout(res, 10)); // Let the browser breathe
          }
        };

        await addRowsInChunks();

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${title || "LoanLinq"}.xlsx`);
      } catch (err) {
        console.error("Excel export failed", err);
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  return { exportToExcel, isExporting };
}
